/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Grid } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormTextField,
  ReactHookFormNumberField,
  isLatitude,
  isLongitude,
  isNonOrBothCoordinates,
  DegreesMinutesField,
} from '@opengeoweb/form-fields';
import type { FormFieldProps } from '../../../types';
import { useSigmetAirmetTranslation } from '../../../utils/i18n';
import { styles } from '../ProductForm.styles';

interface VolcanicFieldsProps extends FormFieldProps {
  helperText: string;
  degreesAndMinutes?: boolean;
}

const VolcanicFields: React.FC<VolcanicFieldsProps> = ({
  isDisabled,
  isReadOnly,
  helperText,
  degreesAndMinutes,
}: VolcanicFieldsProps) => {
  const { t } = useSigmetAirmetTranslation();
  const { watch, trigger, getValues } = useFormContext();

  return (
    <Grid container spacing={2}>
      <Grid
        container
        offset={4}
        size={8}
        spacing={0}
        sx={{
          justifyContent: 'flex-start',

          '.MuiFormHelperText-root': {
            marginTop: '2px',
          },
        }}
      >
        {(!isReadOnly || !!watch('vaSigmetVolcanoName')) && (
          <Grid size={12} sx={styles.volcanicField}>
            <ReactHookFormTextField
              disabled={isDisabled}
              helperText={helperText}
              isReadOnly={isReadOnly}
              label={t('volcano-name')}
              name="vaSigmetVolcanoName"
              rules={{ required: false }}
              size="small"
              upperCase={true}
            />
          </Grid>
        )}
        {(!isReadOnly || !!watch('vaSigmetVolcanoCoordinates.latitude')) && (
          <Grid size={6}>
            {degreesAndMinutes ? (
              <DegreesMinutesField
                disabled={isDisabled}
                isReadOnly={isReadOnly}
                label={t('latitude-degrees-and-minutes')}
                name="vaSigmetVolcanoCoordinates.latitude"
              />
            ) : (
              <ReactHookFormNumberField
                disabled={isDisabled}
                inputMode="decimal"
                isReadOnly={isReadOnly}
                label={t('latitude')}
                name="vaSigmetVolcanoCoordinates.latitude"
                onChange={async (): Promise<boolean> =>
                  trigger('vaSigmetVolcanoCoordinates.longitude')
                }
                rules={{
                  required: true,
                  validate: {
                    isLatitude,
                    isNonOrBothCoordinates: (value): boolean | string =>
                      isNonOrBothCoordinates(
                        value,
                        getValues('vaSigmetVolcanoCoordinates.longitude'),
                      ),
                  },
                }}
                size="small"
                sx={styles.latitude}
              />
            )}
          </Grid>
        )}

        {(!isReadOnly || !!watch('vaSigmetVolcanoCoordinates.longitude')) && (
          <Grid size={6}>
            {degreesAndMinutes ? (
              <DegreesMinutesField
                disabled={isDisabled}
                isLongitude={true}
                isReadOnly={isReadOnly}
                label={t('longitude-degrees-and-minutes')}
                name="vaSigmetVolcanoCoordinates.longitude"
              />
            ) : (
              <ReactHookFormNumberField
                disabled={isDisabled}
                inputMode="decimal"
                isReadOnly={isReadOnly}
                label={t('longitude')}
                name="vaSigmetVolcanoCoordinates.longitude"
                onChange={async (): Promise<boolean> =>
                  trigger('vaSigmetVolcanoCoordinates.latitude')
                }
                rules={{
                  required: true,
                  validate: {
                    isLongitude,
                    isNonOrBothCoordinates: (value): boolean | string =>
                      isNonOrBothCoordinates(
                        value,
                        getValues('vaSigmetVolcanoCoordinates.latitude'),
                      ),
                  },
                }}
                size="small"
                sx={styles.latitude}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default VolcanicFields;
